import { useState, useEffect, useCallback, useRef } from 'react';
import { DefaultService } from '../generated-api';

export interface PlayerRatingInfo {
  rating: number;
  sigma: number;
  gamesPlayed: number;
  isRatingUncertain: boolean;
}

interface UseRecentPlayersReturn {
  recentPlayerIds: string[];
  /** Map of player ID to their last play date (ISO string) */
  lastPlayedMap: Record<string, string>;
  /** Map of player ID to their global league rating info */
  playerRatingsMap: Record<string, PlayerRatingInfo>;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;
const CACHE_KEY = 'recentPlayers';

interface CachedData {
  playerIds: string[];
  lastPlayedMap: Record<string, string>;
  playerRatingsMap: Record<string, PlayerRatingInfo>;
  timestamp: number;
}

const getCachedData = (): CachedData | null => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: CachedData = JSON.parse(cached);
    if (Date.now() - data.timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const setCachedData = (
  playerIds: string[],
  lastPlayedMap: Record<string, string>,
  playerRatingsMap: Record<string, PlayerRatingInfo>
) => {
  const data: CachedData = {
    playerIds,
    lastPlayedMap,
    playerRatingsMap,
    timestamp: Date.now(),
  };
  sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
};

/**
 * Hook to get recent player IDs from recent plays.
 * Returns player IDs ordered by recency (most recently played first),
 * plus a map of player ID to their last play date and ratings.
 */
export const useRecentPlayers = (limit: number = 20): UseRecentPlayersReturn => {
  const [recentPlayerIds, setRecentPlayerIds] = useState<string[]>([]);
  const [lastPlayedMap, setLastPlayedMap] = useState<Record<string, string>>({});
  const [playerRatingsMap, setPlayerRatingsMap] = useState<Record<string, PlayerRatingInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchPromise = useRef<Promise<void> | null>(null);

  const fetchRecentPlayers = useCallback(async (force = false) => {
    // Check cache first
    if (!force) {
      const cached = getCachedData();
      if (cached) {
        setRecentPlayerIds(cached.playerIds.slice(0, limit));
        setLastPlayedMap(cached.lastPlayedMap);
        setPlayerRatingsMap(cached.playerRatingsMap || {});
        setLoading(false);
        return;
      }
    }

    // Deduplicate concurrent requests
    if (fetchPromise.current) {
      return fetchPromise.current;
    }

    setLoading(true);
    setError(null);

    const promise = (async () => {
      try {
        // Fetch recent plays and leagues in parallel
        const [plays, leagues] = await Promise.all([
          DefaultService.getPlaysPlaysGet({
            limit: 50, // Fetch enough to get a good set of recent players
            sort: 'newest'
          }),
          DefaultService.getLeaguesLeaguesGet({})
        ]);

        // Find the global league
        const globalLeague = leagues.find(l => l.isGlobal);

        // Extract unique playerIds preserving recency order
        const playerIds: string[] = [];
        const playedMap: Record<string, string> = {};

        for (const play of plays) {
          // Get all player IDs from all teams in this play
          const playPlayerIds = Object.values(play.teams).flat();

          for (const playerId of playPlayerIds) {
            if (!playerIds.includes(playerId)) {
              playerIds.push(playerId);
              // Record the first (most recent) play date for this player
              playedMap[playerId] = play.playDate;
            }
          }
        }

        // Fetch global league players to get ratings
        const ratingsMap: Record<string, PlayerRatingInfo> = {};
        if (globalLeague) {
          try {
            const leaguePlayers = await DefaultService.getLeaguePlayersLeaguesLeagueIdPlayersGet({
              leagueId: globalLeague.id
            });
            for (const lp of leaguePlayers) {
              ratingsMap[lp.playerId] = {
                rating: lp.rating,
                sigma: lp.sigma,
                gamesPlayed: lp.gamesPlayed,
                isRatingUncertain: lp.gamesPlayed < 5 || lp.sigma > 5
              };
            }
          } catch (ratingsErr) {
            console.warn('Could not fetch player ratings:', ratingsErr);
          }
        }

        // Cache the results
        setCachedData(playerIds, playedMap, ratingsMap);

        // Set state with limit applied
        setRecentPlayerIds(playerIds.slice(0, limit));
        setLastPlayedMap(playedMap);
        setPlayerRatingsMap(ratingsMap);
      } catch (err) {
        console.error('Error fetching recent players:', err);
        setError('Failed to load recent players');
      } finally {
        setLoading(false);
        fetchPromise.current = null;
      }
    })();

    fetchPromise.current = promise;
    return promise;
  }, [limit]);

  const refresh = useCallback(() => {
    sessionStorage.removeItem(CACHE_KEY);
    fetchRecentPlayers(true);
  }, [fetchRecentPlayers]);

  useEffect(() => {
    fetchRecentPlayers();
  }, [fetchRecentPlayers]);

  return { recentPlayerIds, lastPlayedMap, playerRatingsMap, loading, error, refresh };
};

export default useRecentPlayers;
